import { memo, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppConfiguration } from '@mews-commander/data-access';
import { PlatformEnvironment } from '@mews/enums';
import { useToggle } from '@mews/framework';
import { createObservable } from '@mews/std';
import { debounce } from '@optimus-web/core';
import { DefaultErrorBoundary } from '../common/DefaultErrorBoundary';
import { backendComponentsStore, ComponentStoreEntry } from './backendComponentsStore';

export const backendComponentClickEventObservable = createObservable<React.MouseEvent<HTMLDivElement>>();

const BULK_RENDER_DELAY = 100;
let queue = [] as (() => void)[];
const runQueue = debounce(() => {
    queue.forEach((fn) => fn());
    queue = [];
}, BULK_RENDER_DELAY);
const addToRenderQueue = (fn: () => void) => {
    queue.push(fn);
    runQueue();
};

const PortalElement = ({ jsx, html, elementId, showErrorAtBoundary }: ComponentStoreEntry) => {
    // clearing the content of the wrapping container as the mounting target, as it is not cleared by react
    // and the container might contain additional elements (case of BE links for example)
    // cleanup is done only once and since it cant be done in effect, it is done using mutable ref

    const cleanupComplete = useRef(false);
    if (!cleanupComplete.current) {
        html.innerText = '';
        cleanupComplete.current = true;
    }
    return (
        <DefaultErrorBoundary showError={showErrorAtBoundary}>
            {createPortal(jsx, html, elementId)}
        </DefaultErrorBoundary>
    );
};

const LazyRenderedBackendComponent = (props: ComponentStoreEntry) => {
    const [wasVisibleAtLeastOnce, setWasVisibleAtLeastOnce] = useState(false);

    useLayoutEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                observer.disconnect();
                addToRenderQueue(() => setWasVisibleAtLeastOnce(true));
            }
        });
        observer.observe(props.html);
        return () => observer.disconnect();
    }, [props.html]);

    return wasVisibleAtLeastOnce ? <PortalElement {...props} /> : null;
};

const BackendComponent = (props: ComponentStoreEntry) => {
    const { environment } = useAppConfiguration();

    const { isLazyRendered } = props;
    if (environment === PlatformEnvironment.Test || !isLazyRendered) return <PortalElement {...props} />;

    return <LazyRenderedBackendComponent {...props} />;
};
export const BackendComponents = memo(() => {
    const componentsRef = useRef(backendComponentsStore.getState().components);
    const [, toggle] = useToggle();

    // Use manual store subscribe, as default zustand subscribe implemented using `use-sync-external-store/shim/with-selector`
    // has a memory leak. This leak is caused by react storing effects and memoized states in its internals.
    // As a result, the whole dom tree is memoized and all the past components with it as they are the part of the tree.
    // This is solved by using ref instead, which is somehow not memoized.
    // This behavior might change in React 18 and it should be then tested.
    useLayoutEffect(() => {
        const unsubscribe = backendComponentsStore.subscribe(({ components }) => {
            componentsRef.current = components;
            toggle(); // render components
        });
        return unsubscribe;
    }, [toggle]);

    const handleClick = backendComponentClickEventObservable.notify;
    return (
        <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div onClick={handleClick}>
                {componentsRef.current.map((props) => (
                    <BackendComponent {...props} key={props.elementId} />
                ))}
            </div>
        </>
    );
});
